/** @jsxImportSource react */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { publicFormsAPI } from '../api/crm'
import FieldInput from '../components/records/FieldInput'
import { initialValues, toRequestData } from '../components/records/fieldValues'
import { getErrorList, getErrorMessage, toFieldErrors } from '../utils/apiErrors'
import Loading from '../components/common/Loading'

/**
 * A public web form (/f/<key>): anyone with the link fills it in and a record is created. No
 * sign-in, and it may be embedded in an iframe on the workspace's own site. Embedded, it posts
 * {type: 'credintel-form-height', height} to the parent whenever its height changes, so the site
 * can size the iframe to fit (no scrolling inside the frame).
 */
const embedded = window.self !== window.top

const PublicFormPage = () => {
  const { key } = useParams()
  const outer = useRef(null)
  useEffect(() => {
    if (!embedded || !outer.current) return undefined
    const post = () => window.parent.postMessage({ type: 'credintel-form-height', height: Math.ceil(outer.current.getBoundingClientRect().height) }, '*')
    const observer = new ResizeObserver(post)
    observer.observe(outer.current)
    post()
    return () => observer.disconnect()
  })
  const { data: form, isLoading, error } = useQuery({ queryKey: ['public-form', key], queryFn: () => publicFormsAPI.get(key), retry: false })
  // the form in the shape the record inputs expect
  const shaped = useMemo(() => form && {
    sections: [{ fields: form.fields.map((f) => ({ fieldKey: f.key, label: f.label, fieldType: f.type, required: f.required,
      options: f.options, placeholder: f.placeholder, helpText: f.helpText })) }],
  }, [form])
  const [values, setValues] = useState(null)
  const [website, setWebsite] = useState('') // hidden from people; bots fill it in
  const [fieldErrors, setFieldErrors] = useState({})
  const current = values || (shaped ? initialValues(shaped) : {})

  const submit = useMutation({
    mutationFn: () => publicFormsAPI.submit(key, { data: toRequestData(shaped, current), startToken: form.startToken, website }),
    onSuccess: (done) => {
      if (done.redirectUrl) window.top.location.href = done.redirectUrl
    },
    onError: (e) => setFieldErrors(toFieldErrors(e)),
  })

  if (isLoading) return <Loading />
  const page = (children) => (
    // embedded: only as tall as the form, so the reported height can shrink too
    <Box ref={outer} sx={{ minHeight: embedded ? 0 : '100vh', bgcolor: 'background.default', display: 'flex', justifyContent: 'center', p: { xs: 1.5, sm: 3 } }}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, width: '100%', maxWidth: 560, alignSelf: 'flex-start' }}>
        {children}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3 }}>Powered by Credintel CRM</Typography>
      </Paper>
    </Box>
  )
  if (error || !form) return page(<Alert severity="info">This form isn’t available. It may have been switched off or moved.</Alert>)
  if (submit.isSuccess) {
    return page(
      <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', py: 3 }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
        <Typography variant="h6">{submit.data.message}</Typography>
      </Stack>,
    )
  }
  const formKeys = new Set(form.fields.map((f) => f.key))
  const otherErrors = submit.error ? getErrorList(submit.error).filter((e) => !formKeys.has(e.split(':')[0])) : []
  return page(
    <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); setFieldErrors({}); submit.mutate() }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{form.title}</Typography>
      {form.intro && <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{form.intro}</Typography>}
      <Stack spacing={2} sx={{ mt: 3, '& .MuiFormControl-root': { width: '100%' } }}>
        {submit.error && (otherErrors.length > 0 || !Object.keys(fieldErrors).length) && (
          <Alert severity="error">{[getErrorMessage(submit.error), ...otherErrors].join(' · ')}</Alert>
        )}
        {shaped.sections[0].fields.map((f) => (
          <FieldInput key={f.fieldKey} field={f} value={current[f.fieldKey]} error={fieldErrors[f.fieldKey]}
            onChange={(v) => setValues({ ...current, [f.fieldKey]: v })} />
        ))}
        {/* honeypot: off-screen, not announced, skipped by keyboard; only bots fill it in */}
        <Box aria-hidden="true" sx={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>
          <label htmlFor="website">Website</label>
          <input id="website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </Box>
        <Button type="submit" variant="contained" size="large" disabled={submit.isPending}>{submit.isPending ? 'Sending…' : 'Send'}</Button>
      </Stack>
    </Box>,
  )
}

export default PublicFormPage
